import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';

import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFlexLayout, useTable } from 'react-table';

import { Checkbox } from 'ui-components/components/checkbox';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import { ActionsDropdown } from './actions-dropdown';
import messages from '../messages';
import style from './style.module.less';

const createDynamicColumns = (columnsToDisplay) => {
  return columnsToDisplay.map((column) => ({
    Header: <FormattedMessage {...column.messageDescriptor} />,
    accessor: column.mappedKey,
    id: column.id,
  }));
};

/**
 * Table component to display inventory warehouse locations with checkbox selection.
 * @param {object} props - Component properties.
 * @param {Array<{id: string, mappedKey: string, messageDescriptor: {defaultMessage: string, description: string, id: string}}>} props.columnsToDisplay - Columns to display in the table.
 * @param {string} props.inventoryWarehouseName - The name of the inventory warehouse.
 * @param {Array<string>} props.selectedIds - Array of selected location IDs.
 * @param {() => void} props.handleSelectAll - Function to handle select all checkbox.
 * @param {(id: string, event?: Event) => void} props.handleSelectOne - Function to handle individual row selection.
 * @param {Array<{inventoryWarehouseId: string, inventoryLocationId: string, area: string, unit: string, shelf: string, bin: string, isActive: boolean, createDate: string, isUnspecified: boolean, isPickable: boolean, barcode: string}>} props.inventoryWarehouseLocations - Array of filtered and sorted inventory warehouse locations.
 * @returns {JSX.Element} The rendered table component.
 */
const Table = ({
  columnsToDisplay,
  inventoryWarehouseName,
  selectedIds,
  handleSelectAll,
  handleSelectOne,
  inventoryWarehouseLocations,
}) => {
  const { formatMessage } = useIntl();
  const selectableLocations = useMemo(
    () => inventoryWarehouseLocations.filter((location) => !location.isUnspecified),
    [inventoryWarehouseLocations],
  );
  const selectableLocationIds = selectableLocations.map((loc) => loc.inventoryLocationId);
  const selectedSelectableCount = selectedIds.filter((id) => selectableLocationIds.includes(id)).length;

  const allSelected = selectableLocationIds.length > 0 && selectedSelectableCount === selectableLocationIds.length;
  const noneSelected = selectedSelectableCount === 0;
  const isIndeterminate = !allSelected && !noneSelected;

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        Header: () => (
          <Checkbox
            ariaLabel={formatMessage(messages.selectAllLocations)}
            checked={allSelected}
            indeterminate={isIndeterminate}
            onChange={handleSelectAll}
          />
        ),
        Cell: ({ row }) => (
          <Checkbox
            ariaLabel={formatMessage(messages.selectLocation, {
              locationId: row.original.inventoryLocationId,
            })}
            checked={selectedIds.includes(row.original.inventoryLocationId) && !row.original.isUnspecified}
            disabled={row.original.isUnspecified}
            inputId={row.original.inventoryLocationId}
            onChange={(_checked, event) => handleSelectOne(row.original.inventoryLocationId, event)}
          />
        ),
        disableSortBy: true,
        width: 32,
      },
      ...createDynamicColumns(columnsToDisplay),
      {
        Header: 'Active',
        accessor: 'isActive',
        className: style['active-column'],
        id: 'isActive',
        Cell: ({ value }) => (value ? <FontAwesomeIcon className={style.check} icon={faCheck} /> : null),
      },
      {
        Header: <FormattedMessage {...messages.warehousesGridHeaderActionsColumn} />,
        accessor: 'actions',
        id: 'actions',
        className: style['actions-column'],
        disableSortBy: true,
        Cell: ({ row }) => <ActionsDropdown inventoryWarehouseLocation={row.original} inventoryWarehouseName={inventoryWarehouseName} />,
      },
    ],
    [formatMessage, columnsToDisplay, inventoryWarehouseName, allSelected, isIndeterminate, selectedIds, handleSelectAll, handleSelectOne],
  );

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: inventoryWarehouseLocations,
    },
    useFlexLayout,
  );

  const emptyRow = (
    <div className={style['empty-row']}>
      <FormattedMessage {...messages.manageLocationsModalGridPlaceholderText} />
    </div>
  );

  return (
    <ReactTableWrapper
      className={style['table-container']}
      emptyRow={emptyRow}
      getTableBodyProps={getTableBodyProps}
      getTableProps={getTableProps}
      headerGroups={headerGroups}
      prepareRow={prepareRow}
      rows={rows}
    />
  );
};

export { Table };
